import { FilterValues, SearchPanelFilterPill } from '@Components/Search/SearchPanel/types'
import { FormFieldTypes } from '@App/enum/form-field-types'
import { getHoldText, getHoldTypeIdFromName } from '@App/helpers/work-entities/utils/is-hold-status'
import { FilterFieldGroup } from '@App/forms/common/filter-field-group'
import { DiscriminatedFormFields } from '@App/forms/common/discriminated-form-fields'
import { CheckboxGroupDiscriminatedProps } from '@App/forms/common/checkbox-group-form-field'
import { RadioDiscriminatedProps } from '@App/forms/common/radio-discriminated-form-field'
import { DropdownDiscriminatedProps } from '@App/forms/common/dropdown-form-field'

/** Builds the pill removal handler for a single filter field/value pair. */
const buildRemoveHandler =
    <K extends object>(
        appliedFilterValues: Record<string, unknown> | undefined,
        fieldName: string,
        valueKey: string,
        onRemoveFilter: (filters: FilterValues<K> | undefined) => void
    ) =>
    () => {
        const nextFilter = { ...appliedFilterValues }
        if (Array.isArray(nextFilter[fieldName])) {
            nextFilter[fieldName] = (nextFilter[fieldName] as unknown[]).filter((item) => item !== valueKey)
        } else if (typeof nextFilter[fieldName] === 'string') {
            nextFilter[fieldName] = ''
        } else if (typeof nextFilter[fieldName] === 'boolean') {
            nextFilter[fieldName] = false
        }
        onRemoveFilter(nextFilter as FilterValues<K>)
    }

type PushPill = (valueKey: string, labelText: string) => void

const isEmptyValueKey = (valueKey: unknown): boolean => valueKey === '' || valueKey === null || valueKey === undefined

const addCheckboxPill = (fieldConfig: DiscriminatedFormFields, fieldName: string, pushPill: PushPill): void => {
    pushPill(fieldName, (fieldConfig.children || fieldConfig.label || fieldName) as string)
}

const addCheckboxGroupPills = (
    fieldConfig: CheckboxGroupDiscriminatedProps<never>,
    fieldValue: unknown[],
    pushPill: PushPill
): void => {
    fieldValue.forEach((valueKey: unknown) => {
        if (isEmptyValueKey(valueKey)) {
            return
        }
        const option = fieldConfig.checkboxes?.find((checkbox) => checkbox.id === valueKey)
        const labelText =
            option?.children && typeof option.children === 'string'
                ? option.children
                : option?.label || String(valueKey)
        pushPill(String(valueKey), labelText)
    })
}

const addRadioPill = (fieldConfig: RadioDiscriminatedProps, fieldValue: string, pushPill: PushPill): void => {
    const option = fieldConfig.options?.find((radioOption) => radioOption.name === fieldValue)
    pushPill(fieldValue, option?.label || fieldValue)
}

/** Label for a single dropdown/multi-dropdown value, falling back to the raw value. */
const dropdownOptionLabel = (fieldConfig: DropdownDiscriminatedProps, valueKey: string): string => {
    const option = fieldConfig.options?.find((dropdownOption) => String(dropdownOption.value) === valueKey)
    return option?.label || valueKey
}

const addDropdownPill = (fieldConfig: DropdownDiscriminatedProps, fieldValue: string, pushPill: PushPill): void => {
    pushPill(fieldValue, dropdownOptionLabel(fieldConfig, fieldValue))
}

const addMultiDropdownPills = (
    fieldConfig: DropdownDiscriminatedProps,
    fieldValue: unknown[],
    pushPill: PushPill
): void => {
    fieldValue.forEach((valueKey) => {
        if (isEmptyValueKey(valueKey)) {
            return
        }
        const valueStr = String(valueKey)
        pushPill(valueStr, dropdownOptionLabel(fieldConfig, valueStr))
    })
}

const addMultiValuePills = (fieldName: string, fieldValue: unknown[], pushPill: PushPill): void => {
    fieldValue.forEach((valueKey) => {
        if (!valueKey) {
            return
        }
        const valueStr = String(valueKey)
        const holdTypeId = fieldName === 'workHoldStatusFilter' ? getHoldTypeIdFromName(valueStr) : null
        const labelText = holdTypeId?.[0] ? (getHoldText(holdTypeId[0]) ?? valueStr) : valueStr
        pushPill(valueStr, labelText)
    })
}

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value !== ''

/** Routes a single field's applied value to the matching pill builder(s). */
const appendPillsForField = (
    fieldConfig: DiscriminatedFormFields & { name: PropertyKey },
    fieldName: string,
    fieldValue: unknown,
    pushPill: PushPill
): void => {
    if (fieldConfig.type === FormFieldTypes.CHECKBOX && fieldValue === true) {
        addCheckboxPill(fieldConfig, fieldName, pushPill)
    } else if (fieldConfig.type === FormFieldTypes.CHECKBOX_GROUP && Array.isArray(fieldValue)) {
        addCheckboxGroupPills(fieldConfig, fieldValue, pushPill)
    } else if (fieldConfig.type === FormFieldTypes.RADIO && isNonEmptyString(fieldValue)) {
        addRadioPill(fieldConfig, fieldValue, pushPill)
    } else if (fieldConfig.type === FormFieldTypes.DROPDOWN && isNonEmptyString(fieldValue)) {
        addDropdownPill(fieldConfig, fieldValue, pushPill)
    } else if (fieldConfig.type === FormFieldTypes.MULTI_DROPDOWN && Array.isArray(fieldValue)) {
        addMultiDropdownPills(fieldConfig, fieldValue, pushPill)
    } else if (Array.isArray(fieldValue)) {
        addMultiValuePills(fieldName, fieldValue, pushPill)
    }
}

/** Builds the pills contributed by a single field config for the current applied filter values. */
const buildFieldPills = <K extends object>(
    groupName: string,
    fieldConfig: (DiscriminatedFormFields & { name: keyof K }) | undefined,
    appliedFilterValues: Record<string, unknown> | undefined,
    onRemoveFilter: (filters: FilterValues<K> | undefined) => void
): SearchPanelFilterPill[] => {
    if (!fieldConfig?.name) {
        return []
    }

    const fieldName = String(fieldConfig.name)
    const fieldValue = appliedFilterValues?.[fieldName]
    if (fieldValue === undefined || fieldValue === null || fieldValue === '' || fieldValue === false) {
        return []
    }

    const pills: SearchPanelFilterPill[] = []
    const pushPill: PushPill = (valueKey, labelText) => {
        pills.push({
            key: `${fieldName}-${valueKey}`,
            label: `${groupName}: ${labelText}`,
            value: valueKey,
            onRemove: buildRemoveHandler(appliedFilterValues, fieldName, valueKey, onRemoveFilter),
        })
    }

    appendPillsForField(fieldConfig, fieldName, fieldValue, pushPill)

    return pills
}

/** Derives the active filter pills from the applied filter values (not the sidebar's draft state). */
export const buildFilterSummary = <K extends object>(
    filterFieldCollections: Array<FilterFieldGroup<K>> | undefined,
    appliedFilterValues: Record<string, unknown> | undefined,
    onRemoveFilter: (filters: FilterValues<K> | undefined) => void,
    /** A field driven by its own dedicated control (e.g. a SortBy dropdown) shouldn't also get a pill. */
    excludeFieldName?: string
): SearchPanelFilterPill[] => {
    if (!filterFieldCollections) {
        return []
    }

    return filterFieldCollections.flatMap((group) =>
        group.filterFields.flatMap((fieldMap) =>
            // A single `filterFields` record can describe more than one field; cover them all.
            Object.values(fieldMap).flatMap((fieldConfig) => {
                if (!fieldConfig || String(fieldConfig.name) === excludeFieldName) {
                    return []
                }

                return buildFieldPills(
                    group.name,
                    fieldConfig as (DiscriminatedFormFields & { name: keyof K }) | undefined,
                    appliedFilterValues,
                    onRemoveFilter
                )
            })
        )
    )
}
